import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// A new self-serve trial must go through Stripe Checkout (card) on the marketing
// site. This page only creates an account directly for the two legit paths:
// the post-checkout claim (welcome email → ?claim=1) and collaborator invites
// (which set kb_pending_invite). Anyone else is routed to pick a plan + check out.
const PRICING_URL = 'https://keobooks.com/pricing';
import {
  Button,
  Card,
  CardSubtitle,
  CardTitle,
  Field,
  Input,
  PasswordInput,
  Wordmark,
} from '@keobook/ui';
import { useAuth } from '../lib/auth';

export function SignUp() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [searchParams] = useSearchParams();

  // When arriving from a collaborator invite, the email is fixed to the invited
  // address — pre-fill and lock it so the account matches the invitation.
  const invitedEmail = searchParams.get('email')?.trim() ?? '';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Checkout-first enforcement: a new self-serve trial must go through Stripe
  // Checkout (card) on the marketing site. Allow direct account creation only
  // for a post-checkout claim (?claim=1), a collaborator invite (which sets
  // kb_pending_invite), or a referral signup (?ref=). Everyone else gets a CTA
  // to pick a plan + check out.
  const isClaim = searchParams.get('claim') === '1';
  const hasRef = !!searchParams.get('ref');
  const hasPendingInvite = (() => {
    try {
      return !!window.localStorage.getItem('kb_pending_invite');
    } catch {
      return false;
    }
  })();
  const allowedHere = isClaim || hasPendingInvite || hasRef;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await signUp(email.trim(), password, fullName.trim());
      navigate('/', { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not create your account.');
    } finally {
      setSubmitting(false);
    }
  }

  // Not a claim / invite / referral → a self-serve trial starts at checkout.
  if (!allowedHere) {
    return (
      <main className="kb-auth-page">
        <div className="kb-auth-container">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Wordmark size="lg" tagline="Smart accounting, powered by AI" />
          </div>
          <Card size="xl">
            <CardTitle>Start your free trial</CardTitle>
            <CardSubtitle>Pick a plan to begin your 14-day free trial — cancel anytime.</CardSubtitle>
            <a
              href={PRICING_URL}
              className="kb-button kb-button--primary kb-button--block"
              style={{ marginTop: '1rem' }}
            >
              See plans &amp; start free trial →
            </a>
            <p className="kb-auth-switch" style={{ marginTop: '1.25rem' }}>
              Already have an account? <Link to="/signin">Sign in →</Link>
            </p>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" tagline="Smart accounting, powered by AI" />
        </div>

        <Card size="xl">
          <CardTitle>Create your account</CardTitle>
          <CardSubtitle>Start with KEOBOOKS — no credit card required for the trial.</CardSubtitle>

          <form onSubmit={onSubmit} noValidate>
            <Field label="Full name" required>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.currentTarget.value)}
                autoComplete="name"
                required
                placeholder="Jane Smith"
              />
            </Field>

            <Field
              label="Email address"
              required
              hint={invitedEmail ? 'This is the address your invitation was sent to.' : undefined}
            >
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoComplete="email"
                required
                placeholder="you@example.com"
                readOnly={!!invitedEmail}
              />
            </Field>

            <Field label="Password" required hint="At least 8 characters.">
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="new-password"
                required
                placeholder="••••••••"
              />
            </Field>

            <Field label="Confirm password" required>
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.currentTarget.value)}
                autoComplete="new-password"
                required
                placeholder="••••••••"
                invalid={confirm.length > 0 && confirm !== password}
              />
            </Field>

            {error && (
              <div role="alert" className="kb-form-error">
                {error}
              </div>
            )}

            <Button block type="submit" disabled={submitting}>
              {submitting ? 'Creating account…' : 'Create my account'}
            </Button>
          </form>

          <p className="kb-auth-switch">
            Already have an account? <Link to="/signin">Sign in →</Link>
          </p>
        </Card>
      </div>
    </main>
  );
}
